import React from 'react';

import { ChevronDown, ChevronRightIcon, HelpCircle, LogOutIcon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import OnboardingSidebarBg from '@/assets/images/onboarding-sidebar-background.png';
import CheckCircle from '@/assets/svgs/check-circle.svg';
import LogoLight from '@/assets/svgs/logo-light.svg';
import { Button } from '@/components/ui/button';
import { capitalizeFirstLetter } from '@/helpers';
import { cn } from '@/lib/utils';
import {
  LogoutConfirmationModal,
  SupportModal,
  ThemeButton,
  useTheme,
} from '@/modules/common/components';
import { useOnboardingUnsavedChangesStore } from '@/modules/onboarding/common/state/use-onboarding-unsaved-changes-store';
import { OnboardingSteps } from '@/modules/onboarding/organization-setup';
import { UnsavedChangesModal } from '@/modules/settings/components/unsaved-changes-modal';
import { LogoutRequestedRootEvent } from '@/router/state/root/component/root-event';
import { useRootActorRef } from '@/router/state/root/util/context/helpers';

interface OnboardingSidebarProps {
  currentStep: OnboardingSteps | string;
}

export const OnboardingSidebar: React.FC<OnboardingSidebarProps> = ({ currentStep }) => {
  const { t } = useTranslation('common');

  const { theme } = useTheme();
  const actorRef = useRootActorRef();
  const isDirty = useOnboardingUnsavedChangesStore(state => state.isDirty);
  const resetDirty = useOnboardingUnsavedChangesStore(state => state.reset);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = React.useState(false);

  const steps = [
    {
      id: OnboardingSteps.Organization,
      label: t('onboarding.steps.organization.title'),
      subtitle: t('onboarding.steps.organization.subtitle'),
    },
    {
      id: OnboardingSteps.Integrations,
      label: t('onboarding.steps.integrations.title'),
      subtitle: t('onboarding.steps.integrations.subtitle'),
    },
    {
      id: OnboardingSteps.Team,
      label: t('onboarding.steps.team.title'),
      subtitle: t('onboarding.steps.team.subtitle'),
    },
    {
      id: OnboardingSteps.Clients,
      label: t('onboarding.steps.clients.title'),
      subtitle: t('onboarding.steps.clients.subtitle'),
    },
    {
      id: OnboardingSteps.Projects,
      label: t('onboarding.steps.projects.title'),
      subtitle: t('onboarding.steps.projects.subtitle'),
    },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  const handleLogoutClick = React.useCallback(() => {
    if (isDirty) {
      setIsUnsavedChangesModalOpen(true);
    }
  }, [isDirty]);

  const handleUnsavedChangesDiscard = React.useCallback(() => {
    setIsUnsavedChangesModalOpen(false);
    resetDirty();
    actorRef.send(new LogoutRequestedRootEvent());
  }, [actorRef, resetDirty]);

  return (
    <>
      <div
        className="fixed inset-y-0 left-0 z-20 flex h-screen w-full max-w-sm flex-col justify-between bg-zinc-900 bg-cover bg-center p-10 max-lg:max-w-[18rem] max-lg:p-7 max-md:max-w-[12rem] max-md:p-5"
        style={{ backgroundImage: `url(${OnboardingSidebarBg})` }}
      >
        <div className="flex flex-col gap-12 max-lg:gap-9 max-md:gap-6">
          <div className="flex items-center gap-2">
            <img
              alt={t('contentDescription.productLogo')}
              src={LogoLight}
              className="size-5 max-md:size-4"
            />
            <span className="text-lg font-semibold text-sidebar-foreground max-lg:text-[1rem] max-md:text-base">
              {t('productName')}
            </span>
          </div>

          <div className="mt-1 flex flex-col gap-6 max-lg:gap-5 max-md:gap-4">
            {steps.map((step, idx) => {
              const isActive = currentStep === step.id;
              const isPassed = idx < currentStepIndex;

              return (
                <div key={step.id} className="items-top flex gap-2 max-lg:gap-1.5 max-md:gap-1.5">
                  <div
                    className={cn(
                      'flex items-center justify-center size-6 max-lg:size-5 max-md:size-5 rounded-full text-xs font-semibold',
                      isActive
                        ? 'bg-sidebar-foreground text-zinc-900'
                        : isPassed
                          ? 'bg-transparent'
                          : 'text-muted-foreground border-2 border-muted-foreground'
                    )}
                  >
                    {isPassed ? (
                      <img
                        alt={t('onboarding.passedStep')}
                        src={CheckCircle}
                        className="text-green-700"
                      />
                    ) : (
                      idx + 1
                    )}
                  </div>

                  <div className="flex flex-1 flex-col">
                    <span
                      className={cn(
                        'text-sm max-lg:text-xs max-md:text-xs font-medium mt-0.5',
                        isActive ? 'text-sidebar-foreground' : 'text-muted-foreground'
                      )}
                    >
                      {step.label}
                    </span>
                    {isActive && step.subtitle && (
                      <span className="mt-1 text-xs text-muted-foreground max-lg:text-[0.6875rem] max-lg:leading-snug max-md:text-[0.625rem] max-md:leading-tight">
                        {step.subtitle}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-2 max-lg:space-y-1.5 max-md:space-y-1">
          <SupportModal>
            <Button
              variant="ghost"
              className="static flex h-10 w-full content-start items-center gap-2 p-2 text-sm font-normal text-sidebar-foreground hover:bg-white/10 hover:text-sidebar-foreground max-lg:h-9 max-lg:gap-1.5 max-lg:p-1.5 max-lg:text-xs max-md:h-8 max-md:gap-1 max-md:p-1.5 max-md:text-xs"
            >
              <HelpCircle className="size-4 max-lg:size-3 max-md:size-3" />

              <span className="flex-1 text-start">{t('common:sidebar.user.support')}</span>
            </Button>
          </SupportModal>

          <ThemeButton>
            <Button
              variant="ghost"
              className="static flex h-10 w-full content-start items-center gap-2 p-2 text-sm font-normal text-sidebar-foreground hover:bg-white/10 hover:text-sidebar-foreground max-lg:h-9 max-lg:gap-1.5 max-lg:p-1.5 max-lg:text-xs max-md:h-8 max-md:gap-1 max-md:p-1.5 max-md:text-xs"
            >
              <Sun className="size-4 max-lg:size-3 max-md:size-3" />

              <span className="flex-1 text-start">{t('common:sidebar.appearance')}</span>

              <span className="ml-auto flex items-center gap-0.5">
                <span className="max-lg:hidden">{capitalizeFirstLetter(theme)}</span>
                <ChevronDown className="size-4 max-lg:size-3 max-md:size-3" />
              </span>
            </Button>
          </ThemeButton>

          {isDirty ? (
            <Button
              variant="ghost"
              onClick={handleLogoutClick}
              className="static flex h-10 w-full content-start items-center gap-2 p-2 text-sm font-normal text-sidebar-foreground hover:bg-white/10 hover:text-sidebar-foreground max-lg:h-9 max-lg:gap-1.5 max-lg:p-1.5 max-lg:text-xs max-md:h-8 max-md:gap-1 max-md:p-1.5 max-md:text-xs"
            >
              <LogOutIcon className="size-4 max-lg:size-3 max-md:size-3" />

              <span className="flex-1 text-start">{t('common:logout')}</span>

              <ChevronRightIcon className="size-4 max-lg:size-3 max-md:size-3" />
            </Button>
          ) : (
            <LogoutConfirmationModal
              trigger={
                <Button
                  variant="ghost"
                  className="static flex h-10 w-full content-start items-center gap-2 p-2 text-sm font-normal text-sidebar-foreground hover:bg-white/10 hover:text-sidebar-foreground max-lg:h-9 max-lg:gap-1.5 max-lg:p-1.5 max-lg:text-xs max-md:h-8 max-md:gap-1 max-md:p-1.5 max-md:text-xs"
                >
                  <LogOutIcon className="size-4 max-lg:size-3 max-md:size-3" />

                  <span className="flex-1 text-start">{t('common:logout')}</span>

                  <ChevronRightIcon className="size-4 max-lg:size-3 max-md:size-3" />
                </Button>
              }
            />
          )}
        </div>
      </div>

      <UnsavedChangesModal
        open={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onDiscard={handleUnsavedChangesDiscard}
      />
    </>
  );
};
